import { Head, useForm } from "@inertiajs/react";
import { LoaderCircle, Upload } from "lucide-react";
import type { FormEventHandler } from "react";
import Dropzone from "shadcn-dropzone";

import InputError from "@/components/input-error";
import { TemplateDownloadBanner } from "@/components/template-download-banner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FilePreview } from "@/components/ui/file-preview";
import { ACCEPTED_FILE_TYPES } from "@/constants/file";
import { SECTORS_TEMPLATE_URL } from "@/constants/templates";
import AppLayout from "@/layouts/app-layout";
import { sectorsPath } from "@/routes";
import type { BreadcrumbItem } from "@/types";

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Sectores", href: sectorsPath() },
  { title: "Importar", href: "/sectors/import" },
];

export default function Import() {
  const { data, setData, post, processing, errors, reset } = useForm({
    file: null as File | null,
  });

  const submit: FormEventHandler = (e) => {
    e.preventDefault();
    if (!data.file) return;
    post("/sectors/import", { forceFormData: true, onFinish: () => reset("file") });
  };

  const handleFileChange = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0] || null;
    setData("file", file);
  };

  const handleFileRemove = () => {
    setData("file", null);
  };

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Importar Sectores" />
      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <div className="flex flex-col space-y-2">
          <h2 className="text-2xl font-bold">Importar Sectores</h2>
          <p className="text-muted-foreground">Cargar archivo Excel o CSV para importar sectores (crea o actualiza sin eliminar).</p>
        </div>
     <TemplateDownloadBanner
          title="Plantilla de Sectores/Obras (Excel)"
          description="Descargá el Excel de ejemplo. Usá los mismos Sectores/Obras que en Solicitudes de Compra para asegurar el match."
          href={SECTORS_TEMPLATE_URL}
          buttonLabel="Abrir plantilla de Sectores/Obras"
        />

        <div className="max-w-2xl">
          <Card>
            <CardHeader>
              <CardTitle>Cargar Archivo de Sectores</CardTitle>
              <CardDescription>Seleccioná un archivo Excel (.xlsx, .xls) o CSV (.csv) con los sectores a importar</CardDescription>
            </CardHeader>
            <CardContent>
              <form className="flex flex-col gap-6" onSubmit={submit}>
                <div className="grid gap-6">
                  <div className="grid gap-2">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-medium text-blue-900 mb-2">Formato del archivo</h4>
                      <p className="text-sm text-blue-800 mb-3">El archivo debe contener las siguientes columnas:</p>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• <strong>Codigo</strong> (recomendado) - Código único del sector/obra. Se extrae el número inicial si viene junto al texto.</li>
                        <li>• <strong>Descripcion Obra</strong> (requerido si no hay Código) - Nombre/Descripción del sector.</li>
                        <li>• <strong>Obra</strong> (opcional) - Número de obra (se usa para generar código si falta).</li>
                        <li>• <strong>Tipo Obra</strong> (opcional)</li>
                        <li>• <strong>Cliente</strong> (opcional)</li>
                        <li>• <strong>Fecha Inicio</strong>, <strong>Fecha Entrega</strong>, <strong>Fecha Finalizacion</strong> (opcional) - Formato DD/MM/AAAA.</li>
                        <li>• <strong>Unidad Operativa</strong> (opcional)</li>
                        <li>• <strong>Jefe de Obra</strong> (opcional)</li>
                        <li>• <strong>Activa?</strong> (opcional) - SI/NO</li>
                        <li>• <strong>Valor Obra</strong> (opcional) - Monto numérico (se ignoran símbolos)</li>
                      </ul>
                      <p className="text-xs text-blue-700 mt-3">El importador crea nuevos o actualiza existentes por Código (o por Nombre si no hay Código). Nunca elimina.</p>
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <div className="w-full">
                      <Dropzone
                        onDrop={handleFileChange}
                        accept={ACCEPTED_FILE_TYPES.EXCEL}
                        maxFiles={1}
                        disabled={processing}
                      >
                        {() => (
                          <div className="flex justify-center items-center w-full h-32 border-dashed border-2 border-gray-200 rounded-lg hover:bg-accent hover:text-accent-foreground transition-all select-none cursor-pointer">
                            <div className="flex items-center flex-col gap-1.5">
                              <div className="flex items-center flex-row gap-0.5 text-sm font-medium">
                                <Upload className="mr-2 h-4 w-4" />
                                Subir archivo
                              </div>
                              <p className="text-xs text-muted-foreground">Arrastrá y soltá o hacé clic para seleccionar</p>
                            </div>
                          </div>
                        )}
                      </Dropzone>

                      {data.file && (
                        <div className="flex flex-col gap-2 w-full h-fit mt-2 pb-2">
                          <div className="w-full">
                            <FilePreview file={data.file} onRemove={handleFileRemove} disabled={processing} iconColor="text-green-600" />
                          </div>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">Solo se permite un archivo Excel (.xlsx, .xls) o CSV (.csv)</p>
                    <InputError message={errors.file} />
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <Button
                    type="submit"
                    disabled={processing || !data.file}
                    onClick={(e) => {
                      if (!window.confirm("Esto procesará el archivo e importará los sectores. ¿Continuar?")) {
                        e.preventDefault();
                      }
                    }}
                  >
                    {processing && <LoaderCircle className="h-4 w-4 animate-spin" />}
                    Importar Sectores
                  </Button>
                  <Button type="button" variant="outline" onClick={() => window.history.back()} disabled={processing}>
                    Cancelar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}

